import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import nacl from "tweetnacl";
import {
  APIError,
  apiRequest,
  readUISettings,
  resolveAPIBaseURLCandidates,
  writeUISettings,
  type UISettings,
} from "@/lib/api";
import {
  AUTH_USER_STORAGE_KEY,
  findStoredAuthIdentityByUserID,
  findStoredAuthIdentityByUsername,
  readStoredAuthIdentity,
  readStoredAuthIdentities,
  readStoredAuthSession,
  upsertStoredAuthIdentity,
  writeStoredAuthIdentity,
  writeStoredAuthSession,
  type StoredAuthIdentity,
} from "@/lib/auth-storage";
import {
  hostedLoginWithAuth0,
  hostedLoginWithGitHub,
  hostedLoginWithGoogleCode,
  hostedLoginWithPassword,
  hostedLoginWithGoogle,
  hostedLogout,
  hostedReadSessionUser,
  hostedSignupWithPassword,
} from "@/lib/cloud-auth";
import {
  auth0Enabled,
  consumeHostedAuth0Callback,
  hostedAuthRouteOwnsCallback,
  isHostedAuth0CallbackRoute,
  isHostedAuth0RecoveryRoute,
  writeAuth0SwitchFromEmail,
  startHostedAuth0Redirect,
  readAuth0LoggedOutMarker,
  storeHostedAuth0Error,
  writeAuth0LoggedOutMarker,
} from "@/lib/auth0";
import { verifyHostedAuthSession } from "@/lib/hosted-auth-callback";
import { performHostedAuth0Logout } from "@/lib/hosted-auth0-logout";
import { PRODUCT_HOME } from "@/lib/product";
import { isWalletCloudMode } from "@/lib/runtime-mode";
import { emitActiveUserChanged } from "@/lib/user-scope";
import { resetWalletBetaDisclaimerAcceptance } from "@/lib/wallet-beta-disclaimer";

export type DaemonProfile = {
  // profile
  company: string;
  userName: string;
  email: string;
  companyType: "cloud" | "enterprise" | "hybrid";
  // entity role on the network
  entityType: "hub" | "spoke" | "hybrid";
  // runtime
  keepOnPerpetually: boolean;
};

export type AuthUser = {
  id: string;
  username?: string;
  email: string;
  name: string;
  avatarUrl?: string;
  provider: "google" | "github" | "auth0" | "apple" | "email" | "key";
  emailVerified?: boolean;
  emailVerifiedAt?: string;
  daemon?: DaemonProfile;
};

type LoginInput = {
  identifier: string;
  password?: string;
};

type SignupInput = {
  username: string;
  email: string;
  name: string;
  password?: string;
};

type AuthContextValue = {
  user: AuthUser | null;
  ready: boolean;
  login: (input: LoginInput) => Promise<void>;
  signup: (input: SignupInput) => Promise<void>;
  loginWithOAuth: () => Promise<void>;
  signupWithOAuth: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithGitHub: () => Promise<void>;
  logout: () => Promise<void>;
  exportIdentityBackup: (passphrase: string) => Promise<string>;
  importIdentityBackup: (raw: string, passphrase?: string) => Promise<string>;
  completeOnboarding: (profile: DaemonProfile) => void;
  updateUser: (patch: Partial<Pick<AuthUser, "name" | "email" | "emailVerified" | "emailVerifiedAt">>) => void;
  updateDaemon: (patch: Partial<DaemonProfile>) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);
const AUTH_PROFILE_OVERRIDES_KEY = "nebula.auth.profile-overrides.v1";

function readStoredUser(): AuthUser | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(AUTH_USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

function writeStoredUser(user: AuthUser | null) {
  if (typeof window === "undefined") return;
  if (user) window.localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
  else window.localStorage.removeItem(AUTH_USER_STORAGE_KEY);
  emitActiveUserChanged(user?.id || "");
}

function readProfileNameOverride(userID: string): string {
  if (typeof window === "undefined" || !userID) return "";
  try {
    const raw = window.localStorage.getItem(AUTH_PROFILE_OVERRIDES_KEY);
    if (!raw) return "";
    const parsed = JSON.parse(raw) as Record<string, { name?: unknown }>;
    return String(parsed?.[userID]?.name || "").trim();
  } catch {
    return "";
  }
}

function writeProfileNameOverride(userID: string, name: string) {
  if (typeof window === "undefined" || !userID) return;
  try {
    const raw = window.localStorage.getItem(AUTH_PROFILE_OVERRIDES_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, { name?: string }>) : {};
    parsed[userID] = { ...(parsed[userID] || {}), name: String(name || "").trim() };
    window.localStorage.setItem(AUTH_PROFILE_OVERRIDES_KEY, JSON.stringify(parsed));
  } catch {
    // Profile overrides are best-effort only; auth should keep working without them.
  }
}

function applyProfileOverride(user: AuthUser | null): AuthUser | null {
  if (!user?.id) return user;
  const name = readProfileNameOverride(user.id);
  return name ? { ...user, name } : user;
}

const DEFAULT_DAEMON: DaemonProfile = {
  company: "",
  userName: "",
  email: "",
  companyType: "hybrid",
  entityType: "hybrid",
  keepOnPerpetually: true,
};

function normalizeUsername(raw: string): string {
  let value = String(raw || "").trim().toLowerCase();
  const at = value.indexOf("@");
  if (at > 0) value = value.slice(0, at);
  value = value.replace(/[^a-z0-9._-]+/g, "-");
  value = value.replace(/[-_.]{2,}/g, "-");
  value = value.replace(/^[-_.]+|[-_.]+$/g, "");
  return value.slice(0, 48);
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function base64ToBytes(raw: string): Uint8Array {
  const binary = atob(raw);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

function bufferSourceFromBytes(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function bytesToBase64URL(bytes: Uint8Array): string {
  return bytesToBase64(bytes).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function base64URLToBytes(raw: string): Uint8Array {
  const value = String(raw || "").trim();
  if (!value) return new Uint8Array(0);
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padding = (4 - (normalized.length % 4)) % 4;
  return base64ToBytes(normalized + "=".repeat(padding));
}

function toEd25519PrivateJWK(seed: Uint8Array, publicKey: Uint8Array): JsonWebKey {
  return {
    kty: "OKP",
    crv: "Ed25519",
    key_ops: ["sign"],
    ext: true,
    d: bytesToBase64URL(seed),
    x: bytesToBase64URL(publicKey),
  };
}

function extractEd25519SeedFromJWK(jwk: JsonWebKey): Uint8Array {
  const d = String((jwk as any)?.d || "").trim();
  if (!d) {
    throw new Error("Stored identity is missing private key material.");
  }
  const seed = base64URLToBytes(d);
  if (seed.length !== nacl.sign.seedLength) {
    throw new Error("Stored identity private key has an invalid format.");
  }
  return seed;
}

function deriveEd25519KeyPair(identity: StoredAuthIdentity) {
  const seed = extractEd25519SeedFromJWK(identity.privateKeyJwk);
  const keyPair = nacl.sign.keyPair.fromSeed(seed);
  const derivedPublicKey = bytesToBase64(keyPair.publicKey);
  const expectedPublicKey = String(identity.publicKey || "").trim();
  if (expectedPublicKey && expectedPublicKey !== derivedPublicKey) {
    throw new Error("Stored identity public/private keys do not match.");
  }
  return keyPair;
}

async function ensureWebCrypto() {
  if (typeof window === "undefined" || !window.crypto?.subtle) {
    throw new Error("This browser does not support the required WebCrypto APIs.");
  }
}

async function generateIdentity() {
  const keyPair = nacl.sign.keyPair();
  const seed = keyPair.secretKey.slice(0, nacl.sign.seedLength);
  return {
    publicKey: bytesToBase64(keyPair.publicKey),
    privateKeyJwk: toEd25519PrivateJWK(seed, keyPair.publicKey),
  };
}

async function signChallengeMessage(identity: StoredAuthIdentity, message: string) {
  await ensureWebCrypto();
  const keyPair = deriveEd25519KeyPair(identity);
  const payload = new TextEncoder().encode(message);
  const signature = nacl.sign.detached(payload, keyPair.secretKey);
  return bytesToBase64(signature);
}

type IdentityBackupV1 = {
  version: 1;
  provider: "nebula-decentralized-ed25519";
  username: string;
  user_id?: string;
  public_key: string;
  private_key_jwk: JsonWebKey;
  exported_at: string;
};

function toIdentityBackup(identity: StoredAuthIdentity): IdentityBackupV1 {
  return {
    version: 1,
    provider: "nebula-decentralized-ed25519",
    username: identity.username,
    user_id: identity.userId,
    public_key: identity.publicKey,
    private_key_jwk: identity.privateKeyJwk,
    exported_at: new Date().toISOString(),
  };
}

function parseIdentityBackup(raw: string): StoredAuthIdentity {
  let parsed: any;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error("Backup file is not valid JSON.");
  }
  if (Number(parsed?.version) !== 1) {
    throw new Error("Unsupported identity backup version.");
  }
  if (String(parsed?.provider || "") !== "nebula-decentralized-ed25519") {
    throw new Error("Unsupported identity backup provider.");
  }
  const username = normalizeUsername(String(parsed?.username || ""));
  const userId = String(parsed?.user_id || "").trim();
  const publicKey = String(parsed?.public_key || "").trim();
  const privateKeyJwk = parsed?.private_key_jwk;
  if (!username || !publicKey || !privateKeyJwk || typeof privateKeyJwk !== "object") {
    throw new Error("Backup is missing required identity fields.");
  }
  return {
    username,
    userId,
    publicKey,
    privateKeyJwk,
  };
}

function randomString(size = 20): string {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let out = "";
  const bytes = new Uint8Array(size);
  window.crypto.getRandomValues(bytes);
  for (let i = 0; i < size; i += 1) {
    out += chars[bytes[i] % chars.length];
  }
  return out;
}

function oauthRedirectURIFromLocation(override?: string): string {
  const forced = String(override || "").trim();
  if (forced) return forced;
  const origin = window.location.origin;
  const pathname = window.location.pathname || "/";
  if (pathname.startsWith("/ui")) {
    return `${origin}/ui/`;
  }
  return `${origin}${pathname}`;
}

function parsePopupOAuthError(search: string, hash: string): string {
  const queryParams = new URLSearchParams(search || "");
  const hashParams = new URLSearchParams((hash || "").replace(/^#/, ""));
  const err = String(hashParams.get("error") || queryParams.get("error") || "").trim();
  if (!err) return "";
  const description = String(
    hashParams.get("error_description") || queryParams.get("error_description") || "",
  ).trim();
  return description ? `${err}: ${description}` : err;
}

function isOAuthRedirectURIMismatchError(value: unknown): boolean {
  const text = String((value as any)?.message || value || "").toLowerCase();
  return text.includes("redirect_uri_mismatch");
}

function isGoogleOAuthPopupRetryableError(value: unknown): boolean {
  const text = String((value as any)?.message || value || "").toLowerCase();
  return text.includes("google login was cancelled") || text.includes("google login timed out");
}

function googleRedirectURICandidates(primary?: string, extra?: string[]): string[] {
  const candidates = new Set<string>();
  const add = (value: string) => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return;
    candidates.add(trimmed);
  };
  add(String(primary || ""));
  for (const item of Array.isArray(extra) ? extra : []) {
    add(String(item || ""));
  }
  return Array.from(candidates);
}

function decodeJWTPart(raw: string): Record<string, unknown> {
  const value = String(raw || "").trim();
  if (!value) return {};
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padding = (4 - (normalized.length % 4)) % 4;
  const decoded = atob(normalized + "=".repeat(padding));
  return JSON.parse(decoded) as Record<string, unknown>;
}

function parseGoogleIDTokenProfile(idToken: string): { sub: string; email: string; name: string; picture?: string } {
  const parts = String(idToken || "").split(".");
  if (parts.length < 2) {
    throw new Error("Google did not return a valid ID token.");
  }
  const payload = decodeJWTPart(parts[1]);
  return {
    sub: String(payload.sub || "").trim(),
    email: String(payload.email || "").trim().toLowerCase(),
    name: String(payload.name || payload.given_name || payload.email || "Wallet Owner").trim(),
    picture: String(payload.picture || "").trim() || undefined,
  };
}

async function loginWithGoogleIDTokenFromPopup(clientID: string, redirectURIOverride?: string): Promise<string> {
  if (typeof window === "undefined") throw new Error("Google login requires a browser environment.");
  const origin = window.location.origin;
  const redirectURI = oauthRedirectURIFromLocation(redirectURIOverride);
  const state = randomString(16);
  const nonce = randomString(24);
  const scope = encodeURIComponent("openid email profile");
  const url = [
    "https://accounts.google.com/o/oauth2/v2/auth",
    `?client_id=${encodeURIComponent(clientID)}`,
    `&redirect_uri=${encodeURIComponent(redirectURI)}`,
    "&response_type=id_token",
    `&scope=${scope}`,
    `&state=${encodeURIComponent(state)}`,
    `&nonce=${encodeURIComponent(nonce)}`,
    "&prompt=select_account",
  ].join("");

  const popup = window.open(url, "nebula-google-login", "width=520,height=680");
  if (!popup) throw new Error("Google login popup was blocked. Please allow popups and try again.");

  return new Promise<string>((resolve, reject) => {
    const startedAt = Date.now();
    const timeoutMs = 2 * 60 * 1000;
    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        reject(new Error("Google login was cancelled."));
        return;
      }
      if (Date.now() - startedAt > timeoutMs) {
        window.clearInterval(timer);
        popup.close();
        reject(new Error("Google login timed out."));
        return;
      }
      try {
        const popupURL = popup.location;
        if (popupURL.origin !== origin) return;
        const oauthError = parsePopupOAuthError(popupURL.search || "", popupURL.hash || "");
        if (oauthError) {
          window.clearInterval(timer);
          popup.close();
          reject(new Error(`Google login failed: ${oauthError}`));
          return;
        }
        const hash = popupURL.hash || "";
        if (!hash.includes("id_token=")) return;
        const params = new URLSearchParams(hash.replace(/^#/, ""));
        const idToken = String(params.get("id_token") || "");
        const returnedState = String(params.get("state") || "");
        if (!idToken) {
          window.clearInterval(timer);
          popup.close();
          reject(new Error("Google did not return an ID token."));
          return;
        }
        if (returnedState !== state) {
          window.clearInterval(timer);
          popup.close();
          reject(new Error("Google login state mismatch."));
          return;
        }
        window.clearInterval(timer);
        popup.close();
        resolve(idToken);
      } catch {
        // Ignore cross-origin access errors while popup is on Google origin.
      }
    }, 300);
  });
}

async function loginWithGooglePKCEPopup(
  clientID: string,
  redirectURIOverride?: string,
): Promise<{ code: string; codeVerifier: string; redirectURI: string }> {
  if (typeof window === "undefined") throw new Error("Google login requires a browser environment.");
  await ensureWebCrypto();
  const origin = window.location.origin;
  const redirectURI = oauthRedirectURIFromLocation(redirectURIOverride);
  const state = randomString(20);
  const verifierBytes = new Uint8Array(32);
  window.crypto.getRandomValues(verifierBytes);
  const codeVerifier = bytesToBase64URL(verifierBytes);
  const challengeHash = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier));
  const codeChallenge = bytesToBase64URL(new Uint8Array(challengeHash));

  const scope = encodeURIComponent("openid email profile");
  const url = [
    "https://accounts.google.com/o/oauth2/v2/auth",
    `?client_id=${encodeURIComponent(clientID)}`,
    `&redirect_uri=${encodeURIComponent(redirectURI)}`,
    "&response_type=code",
    `&scope=${scope}`,
    `&state=${encodeURIComponent(state)}`,
    "&access_type=offline",
    "&include_granted_scopes=true",
    "&prompt=select_account",
    "&code_challenge_method=S256",
    `&code_challenge=${encodeURIComponent(codeChallenge)}`,
  ].join("");

  const popup = window.open(url, "nebula-google-login", "width=520,height=680");
  if (!popup) throw new Error("Google login popup was blocked. Please allow popups and try again.");

  return new Promise<{ code: string; codeVerifier: string; redirectURI: string }>((resolve, reject) => {
    const startedAt = Date.now();
    const timeoutMs = 2 * 60 * 1000;
    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        reject(new Error("Google login was cancelled."));
        return;
      }
      if (Date.now() - startedAt > timeoutMs) {
        window.clearInterval(timer);
        popup.close();
        reject(new Error("Google login timed out."));
        return;
      }
      try {
        const popupURL = popup.location;
        if (popupURL.origin !== origin) return;
        const params = new URLSearchParams(popupURL.search || "");
        const code = String(params.get("code") || "");
        const returnedState = String(params.get("state") || "");
        const oauthError = parsePopupOAuthError(popupURL.search || "", popupURL.hash || "");
        if (oauthError) {
          window.clearInterval(timer);
          popup.close();
          reject(new Error(`Google login failed: ${oauthError}`));
          return;
        }
        if (!code) return;
        if (returnedState !== state) {
          window.clearInterval(timer);
          popup.close();
          reject(new Error("Google login state mismatch."));
          return;
        }
        window.clearInterval(timer);
        popup.close();
        resolve({ code, codeVerifier, redirectURI });
      } catch {
        // Ignore cross-origin access errors until popup returns to app origin.
      }
    }, 300);
  });
}

async function loginWithGitHubPKCEPopup(
  clientID: string,
  redirectURIOverride?: string,
): Promise<{ code: string; codeVerifier: string; redirectURI: string }> {
  if (typeof window === "undefined") throw new Error("GitHub login requires a browser environment.");
  await ensureWebCrypto();
  const origin = window.location.origin;
  const redirectURI = oauthRedirectURIFromLocation(redirectURIOverride);
  const state = randomString(20);
  const verifierBytes = new Uint8Array(32);
  window.crypto.getRandomValues(verifierBytes);
  const codeVerifier = bytesToBase64URL(verifierBytes);
  const challengeHash = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier));
  const codeChallenge = bytesToBase64URL(new Uint8Array(challengeHash));

  const scope = encodeURIComponent("read:user user:email");
  const url = [
    "https://github.com/login/oauth/authorize",
    `?client_id=${encodeURIComponent(clientID)}`,
    `&redirect_uri=${encodeURIComponent(redirectURI)}`,
    `&scope=${scope}`,
    `&state=${encodeURIComponent(state)}`,
    "&allow_signup=true",
    "&prompt=select_account",
    "&code_challenge_method=S256",
    `&code_challenge=${encodeURIComponent(codeChallenge)}`,
  ].join("");

  const popup = window.open(url, "nebula-github-login", "width=520,height=760");
  if (!popup) throw new Error("GitHub login popup was blocked. Please allow popups and try again.");

  return new Promise<{ code: string; codeVerifier: string; redirectURI: string }>((resolve, reject) => {
    const startedAt = Date.now();
    const timeoutMs = 2 * 60 * 1000;
    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        reject(new Error("GitHub login was cancelled."));
        return;
      }
      if (Date.now() - startedAt > timeoutMs) {
        window.clearInterval(timer);
        popup.close();
        reject(new Error("GitHub login timed out."));
        return;
      }
      try {
        const popupURL = popup.location;
        if (popupURL.origin !== origin) return;
        const params = new URLSearchParams(popupURL.search || "");
        const code = String(params.get("code") || "");
        const returnedState = String(params.get("state") || "");
        const oauthError = parsePopupOAuthError(popupURL.search || "", popupURL.hash || "");
        if (oauthError) {
          window.clearInterval(timer);
          popup.close();
          reject(new Error(`GitHub login failed: ${oauthError}`));
          return;
        }
        if (!code) return;
        if (returnedState !== state) {
          window.clearInterval(timer);
          popup.close();
          reject(new Error("GitHub login state mismatch."));
          return;
        }
        window.clearInterval(timer);
        popup.close();
        resolve({ code, codeVerifier, redirectURI });
      } catch {
        // Ignore cross-origin access errors until popup returns to app origin.
      }
    }, 300);
  });
}

type EncryptedIdentityBackupV2 = {
  version: 2;
  provider: "nebula-decentralized-ed25519";
  encryption: {
    alg: "AES-GCM";
    kdf: "PBKDF2-SHA256";
    iterations: number;
    salt_b64: string;
    iv_b64: string;
  };
  ciphertext_b64: string;
  exported_at: string;
};

async function deriveBackupKey(passphrase: string, salt: Uint8Array, iterations: number) {
  const material = await window.crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(passphrase),
    { name: "PBKDF2" },
    false,
    ["deriveKey"],
  );
  return window.crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: bufferSourceFromBytes(salt), iterations, hash: "SHA-256" },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

async function encryptIdentityBackup(backup: IdentityBackupV1, passphrase: string): Promise<string> {
  const phrase = String(passphrase || "").trim();
  if (phrase.length < 8) {
    throw new Error("Backup passphrase must be at least 8 characters.");
  }
  await ensureWebCrypto();
  const salt = new Uint8Array(16);
  const iv = new Uint8Array(12);
  window.crypto.getRandomValues(salt);
  window.crypto.getRandomValues(iv);
  const iterations = 210_000;
  const key = await deriveBackupKey(phrase, salt, iterations);
  const plaintext = new TextEncoder().encode(JSON.stringify(backup));
  const ciphertext = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv: bufferSourceFromBytes(iv) }, key, plaintext);
  const envelope: EncryptedIdentityBackupV2 = {
    version: 2,
    provider: "nebula-decentralized-ed25519",
    encryption: {
      alg: "AES-GCM",
      kdf: "PBKDF2-SHA256",
      iterations,
      salt_b64: bytesToBase64(salt),
      iv_b64: bytesToBase64(iv),
    },
    ciphertext_b64: bytesToBase64(new Uint8Array(ciphertext)),
    exported_at: new Date().toISOString(),
  };
  return JSON.stringify(envelope, null, 2);
}

async function decryptIdentityBackup(raw: string, passphrase?: string): Promise<StoredAuthIdentity> {
  let parsed: any;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error("Backup file is not valid JSON.");
  }
  if (Number(parsed?.version) === 1) {
    return parseIdentityBackup(raw);
  }
  if (Number(parsed?.version) !== 2 || String(parsed?.provider || "") !== "nebula-decentralized-ed25519") {
    throw new Error("Unsupported identity backup format.");
  }
  const phrase = String(passphrase || "").trim();
  if (!phrase) throw new Error("This backup is encrypted. Enter the backup passphrase.");
  const encryption = parsed?.encryption || {};
  if (encryption.alg !== "AES-GCM" || encryption.kdf !== "PBKDF2-SHA256") {
    throw new Error("Unsupported backup encryption format.");
  }
  const iterations = Number(encryption.iterations);
  if (!Number.isFinite(iterations) || iterations <= 0) {
    throw new Error("Backup encryption metadata is invalid.");
  }
  await ensureWebCrypto();
  const salt = base64ToBytes(String(encryption.salt_b64 || ""));
  const iv = base64ToBytes(String(encryption.iv_b64 || ""));
  const ciphertext = base64ToBytes(String(parsed?.ciphertext_b64 || ""));
  const key = await deriveBackupKey(phrase, salt, iterations);
  let plaintext: ArrayBuffer;
  try {
    plaintext = await window.crypto.subtle.decrypt({ name: "AES-GCM", iv: bufferSourceFromBytes(iv) }, key, bufferSourceFromBytes(ciphertext));
  } catch {
    throw new Error("Backup passphrase is incorrect or backup is corrupted.");
  }
  const decrypted = new TextDecoder().decode(plaintext);
  return parseIdentityBackup(decrypted);
}

function syncTenantWithUser(userID: string) {
  const user = String(userID || "").trim();
  if (!user) return;
  const settings = readUISettings(user);
  if (settings.tenant_id === user) return;
  writeUISettings({ ...settings, tenant_id: user }, user);
}

function clearTenantBinding(userID?: string) {
  const settings = readUISettings(userID);
  if (!settings.tenant_id) return;
  writeUISettings({ ...settings, tenant_id: "" }, userID);
}

function syncHubBaseWithAuthPayload(payload: { daemon_base_url?: string } | null | undefined, userID?: string) {
  const daemonBaseURL = String(payload?.daemon_base_url || "").trim().replace(/\/$/, "");
  if (!daemonBaseURL) return;
  const settings = readUISettings(userID);
  const currentBase = String(settings.hub_base || "").trim().replace(/\/$/, "");
  if (currentBase === daemonBaseURL) return;
  writeUISettings({ ...settings, hub_base: daemonBaseURL }, userID);
}

function normalizeDaemonBaseURL(raw: string): string {
  return String(raw || "").trim().replace(/\/$/, "");
}

function authSettingsForDaemonBase(baseURL: string): UISettings {
  const base = normalizeDaemonBaseURL(baseURL);
  return {
    ...readUISettings(),
    hub_base: base,
    // Auth discovery must not leak previous user scope to another daemon.
    api_key: "",
    tenant_id: "",
  };
}

function daemonBaseCandidates(preferred?: string): string[] {
  const settings = readUISettings();
  const seen = new Set<string>();
  const out: string[] = [];
  const add = (value: string) => {
    const normalized = normalizeDaemonBaseURL(value);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    out.push(normalized);
  };

  add(preferred || "");
  resolveAPIBaseURLCandidates(settings).forEach(add);
  return out;
}

function daemonBasesFromIdentityPins(identities: StoredAuthIdentity[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (value: string) => {
    const normalized = normalizeDaemonBaseURL(value);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    out.push(normalized);
  };

  identities.forEach((identity) => {
    const userID = String(identity.userId || "").trim();
    if (!userID) return;
    const settings = readUISettings(userID);
    add(String(settings.hub_base || ""));
  });
  return out;
}

function loginDaemonBaseCandidates(localIdentities: StoredAuthIdentity[], hintedIdentity: StoredAuthIdentity | null): string[] {
  const hintedPinnedBase = pinnedBaseForIdentity(hintedIdentity);
  return uniqueDaemonBases([
    hintedPinnedBase,
    ...daemonBasesFromIdentityPins(localIdentities),
    ...daemonBaseCandidates(),
  ]);
}

async function expandLoginDaemonCandidates(candidates: string[]): Promise<string[]> {
  const discovered: string[] = [...candidates];
  for (const candidate of candidates) {
    const settings = authSettingsForDaemonBase(candidate);
    try {
      const payload = await apiRequest<{ peers?: Array<{ api_base_url?: string }> }>(
        "/api/v1/network/peers/available",
        { method: "GET", timeoutMs: 4500 },
        settings,
      );
      const peers = Array.isArray(payload?.peers) ? payload.peers : [];
      peers.forEach((peer) => {
        discovered.push(normalizeDaemonBaseURL(String(peer?.api_base_url || "")));
      });
    } catch {
      // Best-effort discovery only.
    }
  }
  return uniqueDaemonBases(discovered);
}

function isNetworkAuthError(error: unknown): error is APIError {
  return error instanceof APIError && (error.code === "ERR_NETWORK" || error.code === "ERR_TIMEOUT");
}

function uniqueDaemonBases(values: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    const normalized = normalizeDaemonBaseURL(value);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    out.push(normalized);
  }
  return out;
}

function pinnedBaseForIdentity(identity: StoredAuthIdentity | null): string {
  const userID = String(identity?.userId || "").trim();
  if (!userID) return "";
  const settings = readUISettings(userID);
  return normalizeDaemonBaseURL(String(settings.hub_base || ""));
}

function findIdentityByAccount(
  identities: StoredAuthIdentity[],
  username: string,
  userID?: string,
): StoredAuthIdentity | null {
  const normalizedUsername = normalizeUsername(username);
  const normalizedUserID = String(userID || "").trim();

  if (normalizedUserID) {
    const byUserID = identities.find((identity) => String(identity.userId || "").trim() === normalizedUserID)
      || findStoredAuthIdentityByUserID(normalizedUserID);
    if (byUserID) return byUserID;
  }

  if (normalizedUsername) {
    const byUsername = identities.find((identity) => normalizeUsername(identity.username) === normalizedUsername)
      || findStoredAuthIdentityByUsername(normalizedUsername);
    if (byUsername) return byUsername;
  }

  return null;
}

function findIdentityByUserID(identities: StoredAuthIdentity[], userID: string): StoredAuthIdentity | null {
  const normalizedUserID = String(userID || "").trim();
  if (!normalizedUserID) return null;
  return identities.find((identity) => String(identity.userId || "").trim() === normalizedUserID)
    || findStoredAuthIdentityByUserID(normalizedUserID);
}

function findIdentityByPublicKey(
  identities: StoredAuthIdentity[],
  publicKey: string,
): StoredAuthIdentity | null {
  const normalizedPublicKey = String(publicKey || "").trim();
  if (!normalizedPublicKey) return null;
  return identities.find((identity) => String(identity.publicKey || "").trim() === normalizedPublicKey) || null;
}

function findIdentityHintByIdentifier(
  identities: StoredAuthIdentity[],
  identifier: string,
): StoredAuthIdentity | null {
  const normalizedIdentifier = String(identifier || "").trim().toLowerCase();
  if (!normalizedIdentifier) return null;

  if (normalizedIdentifier.includes("@")) {
    const byEmail = identities.find((identity) => String(identity.email || "").trim().toLowerCase() === normalizedIdentifier);
    if (byEmail) return byEmail;
  }

  return findIdentityByAccount(identities, normalizedIdentifier);
}

async function recoverIdentityFromDaemonBackup(
  challengeID: string,
  passphrase: string,
  authSettings: UISettings,
  usernameHint: string,
  userIDHint?: string,
): Promise<StoredAuthIdentity> {
  const challenge = String(challengeID || "").trim();
  if (!challenge) {
    throw new Error("Missing challenge id for identity recovery.");
  }
  const phrase = String(passphrase || "").trim();
  if (!phrase) {
    throw new Error("Account found in the local wallet workspace, but this browser does not have its key. Enter your password to restore it, or import identity backup.");
  }
  const payload = await apiRequest<{
    challenge_id?: string;
    user_id?: string;
    username?: string;
    encrypted_identity_backup?: string;
  }>("/api/v1/auth/login/identity-backup", {
    method: "POST",
    body: { challenge_id: challenge },
  }, authSettings);
  const encrypted = String(payload?.encrypted_identity_backup || "").trim();
  if (!encrypted) {
    throw new Error("This account has no local wallet backup stored yet. Import identity backup to continue.");
  }
  const restored = await decryptIdentityBackup(encrypted, phrase);
  const normalizedUsername = normalizeUsername(String(payload?.username || usernameHint || restored.username || ""));
  const normalizedUserID = String(payload?.user_id || userIDHint || restored.userId || "").trim();
  const normalizedEmail = String(restored.email || "").trim().toLowerCase();
  if (!normalizedUsername) {
    throw new Error("Recovered identity is missing a valid username.");
  }
  return {
    ...restored,
    username: normalizedUsername,
    userId: normalizedUserID || restored.userId,
    email: normalizedEmail || restored.email,
  };
}

async function syncIdentityBackupToDaemon(
  identity: StoredAuthIdentity,
  passphrase: string,
  authSettings: UISettings,
) {
  const phrase = String(passphrase || "").trim();
  if (!phrase) return;
  const encrypted = await encryptIdentityBackup(toIdentityBackup(identity), phrase);
  await apiRequest("/api/v1/auth/identity-backup", {
    method: "POST",
    body: {
      encrypted_identity_backup: encrypted,
    },
  }, authSettings);
}

function loginDiscoveryFailure(
  error: unknown,
  hasPinnedDaemonBase: boolean,
  pinnedDaemonBase: string,
): Error | null {
  if (isNetworkAuthError(error)) {
    if (hasPinnedDaemonBase) {
      return new Error(`Pinned local workspace is unreachable (${pinnedDaemonBase || "unknown"}). Start the wallet app and try again.`);
    }
    return new Error("Could not reach any local workspace for sign-in. Start the wallet app and try again.");
  }
  return error instanceof Error ? error : null;
}

function shouldContinueLoginDiscovery(error: unknown): boolean {
  if (!(error instanceof APIError)) return false;
  if (error.code === "ERR_AUTH_UNKNOWN_USER") return true;
  if (error.code === "ERR_NETWORK" || error.code === "ERR_TIMEOUT") return true;
  return error.status === 404 || error.status === 503;
}

function shouldFallbackFromHostedPasswordAuth(error: unknown): boolean {
  if (!(error instanceof APIError)) return false;
  if (error.code === "ERR_NETWORK" || error.code === "ERR_TIMEOUT") return true;
  return error.status === 404 || error.status === 503;
}

function shouldContinueSignupPlacement(error: unknown): boolean {
  if (!(error instanceof APIError)) return false;
  if (error.code === "ERR_DAEMON_ALREADY_BOUND") return true;
  if (error.code === "ERR_NETWORK" || error.code === "ERR_TIMEOUT") return true;
  return error.status === 404 || error.status === 503;
}

function authUserFromServer(serverUser: any, existing: AuthUser | null): AuthUser {
  const provider = String(serverUser?.provider || "key").trim().toLowerCase();
  const nextID = String(serverUser?.id || "").trim();
  const sameUser = !!existing && String(existing.id || "").trim() === nextID;
  return {
    id: nextID,
    username: String(serverUser?.username || "").trim() || undefined,
    email: String(serverUser?.email || "").trim(),
    name: String(serverUser?.name || serverUser?.username || "Operator").trim(),
    provider: provider === "google" || provider === "github" || provider === "auth0" || provider === "apple" ? provider : "key",
    daemon: sameUser ? existing?.daemon : undefined,
  };
}

function designPreviewUser(): AuthUser | null {
  if (String(import.meta.env.VITE_DESIGN_PREVIEW || "").trim().toLowerCase() !== "true") {
    return null;
  }
  if (typeof window === "undefined") return null;
  const host = window.location.hostname;
  if (host !== "127.0.0.1" && host !== "localhost") return null;
  return {
    id: "design-preview-user",
    username: "justin",
    email: "justin.kirk@nbt.nhs.uk",
    name: "Justin Kirk",
    provider: "auth0",
    daemon: {
      company: "GENRL",
      userName: "Justin Kirk",
      email: "justin.kirk@nbt.nhs.uk",
      companyType: "cloud",
      entityType: "hub",
      keepOnPerpetually: true,
    },
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() =>
    isHostedAuth0CallbackRoute() || isHostedAuth0RecoveryRoute()
      ? null
      : applyProfileOverride(readStoredUser()),
  );
  const [auth0LoggedOut, setAuth0LoggedOut] = useState(() => readAuth0LoggedOutMarker());
  const [ready, setReady] = useState(
    () =>
      isHostedAuth0CallbackRoute() ||
      isHostedAuth0RecoveryRoute() ||
      (Boolean(readStoredUser()) && !readAuth0LoggedOutMarker()),
  );
  const {
    isLoading: auth0Loading,
    isAuthenticated: auth0IsAuthenticated,
    user: auth0User,
    getIdTokenClaims,
  } = useAuth0();

  const persistUser = (next: AuthUser | null) => {
    const merged = applyProfileOverride(next);
    if (merged?.id && merged.id !== user?.id) {
      resetWalletBetaDisclaimerAcceptance();
    }
    setUser(merged);
    writeStoredUser(merged);
  };

  const markAuth0LoggedOut = (value: boolean) => {
    setAuth0LoggedOut(value);
    writeAuth0LoggedOutMarker(value);
  };

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      const previewUser = designPreviewUser();
      if (previewUser) {
        if (!cancelled) {
          persistUser(previewUser);
          syncTenantWithUser(previewUser.id);
          setReady(true);
        }
        return;
      }

      const storedUser = readStoredUser();
      const callbackRoute = isHostedAuth0CallbackRoute();
      const recoveryRoute = isHostedAuth0RecoveryRoute();
      if (!cancelled) {
        setUser(callbackRoute || recoveryRoute ? null : applyProfileOverride(storedUser));
      }

      if (auth0Enabled()) {
        // The dedicated callback route owns the destructive callback exchange.
        // Do not race it with a stale /me bootstrap that can flip `ready` while
        // the callback POST is still in flight.
        if (callbackRoute) {
          if (!cancelled) setReady(true);
          return;
        }
        if (recoveryRoute) {
          writeStoredAuthSession(null);
          if (!cancelled) {
            persistUser(null);
            setReady(true);
          }
          return;
        }
        if (auth0LoggedOut) {
          if (!cancelled) {
            persistUser(null);
            setReady(true);
          }
          return;
        }
        try {
          const hostedUser = storedUser?.id
            ? await verifyHostedAuthSession(storedUser, hostedReadSessionUser)
            : await hostedReadSessionUser();
          if (!cancelled) {
            persistUser(hostedUser);
            if (hostedUser?.id) syncTenantWithUser(hostedUser.id);
          }
        } catch {
          if (!cancelled) {
            persistUser(null);
            if (storedUser?.id && typeof window !== "undefined") {
              storeHostedAuth0Error(
                "Your sign-in completed, but GENRL could not restore the app session. Please try again.",
              );
              window.location.replace(
                `${window.location.origin}/ui/login?auth_error=session_failed`,
              );
              return;
            }
          }
        }
        if (!cancelled) setReady(true);
        return;
      }

      const session = readStoredAuthSession();
      if (session?.token) {
        try {
          const payload = await apiRequest<{ user: any; session_expires_at: string; daemon_base_url?: string }>("/api/v1/auth/me", {
            headers: { Authorization: `Bearer ${session.token}` },
            timeoutMs: 10000,
          });
          if (!cancelled) {
            const nextUser = authUserFromServer(payload.user, storedUser);
            syncHubBaseWithAuthPayload(payload, nextUser.id);
            persistUser(nextUser);
            writeStoredAuthSession({
              token: session.token,
              expiresAt: String(payload.session_expires_at || session.expiresAt || ""),
            });
            syncTenantWithUser(nextUser.id);
          }
        } catch {
          writeStoredAuthSession(null);
          if (!cancelled) {
            persistUser(null);
          }
        }
      }
      if (!cancelled) setReady(true);
    };
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [auth0IsAuthenticated, auth0Loading, auth0LoggedOut]);

  useEffect(() => {
    if (!auth0Enabled()) return;
    if (hostedAuthRouteOwnsCallback()) return;
    const callback = consumeHostedAuth0Callback();
    if (!callback) return;

    let cancelled = false;
    const syncHostedCallback = async () => {
      if (callback.error) {
        if (!cancelled) {
          persistUser(null);
          setReady(true);
          if (typeof window !== "undefined") {
            window.location.replace(
              `${window.location.origin}/ui/login?auth_error=hosted_callback_failed`,
            );
          }
        }
        return;
      }
      if (!callback.idToken) return;
      try {
        if (!cancelled) setReady(false);
        const disallowedEmail = String(callback.disallowedEmail || "")
          .trim()
          .toLowerCase();
        const nextUser = await hostedLoginWithAuth0({
          idToken: callback.idToken,
          expectedEmail: callback.expectedEmail,
          disallowedEmail,
        });
        if (disallowedEmail && String(nextUser.email || "").trim().toLowerCase() === disallowedEmail) {
          await hostedLogout().catch(() => undefined);
          writeAuth0SwitchFromEmail("");
          markAuth0LoggedOut(true);
          persistUser(null);
          storeHostedAuth0Error(
            "The previous account was selected again. Retry to sign in with it normally, or choose another account.",
          );
          if (!cancelled && typeof window !== "undefined") {
            window.location.replace(
              `${window.location.origin}/ui/login?auth_error=hosted_callback_failed`,
            );
          }
          return;
        }
        const localIdentities = readStoredAuthIdentities();
        const existingIdentity = findIdentityByUserID(localIdentities, nextUser.id)
          || findIdentityByAccount(localIdentities, nextUser.username || nextUser.email, nextUser.id);
        if (!existingIdentity) {
          const identity = await generateIdentity();
          upsertStoredAuthIdentity({
            username: nextUser.username || normalizeUsername(nextUser.email),
            userId: nextUser.id,
            email: nextUser.email || undefined,
            publicKey: identity.publicKey,
            privateKeyJwk: identity.privateKeyJwk,
          });
        }
        if (!cancelled) {
          markAuth0LoggedOut(false);
          writeAuth0SwitchFromEmail("");
          persistUser(nextUser);
          syncTenantWithUser(nextUser.id);
        }
      } catch (error) {
        storeHostedAuth0Error(
          error instanceof Error && error.message.trim()
            ? error.message
            : "Hosted sign-in could not be completed. Please try again.",
        );
        if (!cancelled) {
          persistUser(null);
          writeAuth0SwitchFromEmail("");
          if (typeof window !== "undefined") {
            window.location.replace(
              `${window.location.origin}/ui/login?auth_error=hosted_callback_failed`,
            );
          }
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    };

    syncHostedCallback();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isWalletCloudMode()) return;
    if (!auth0Enabled()) return;
    if (auth0Loading) return;
    if (auth0LoggedOut) return;
    if (!auth0IsAuthenticated || !auth0User?.sub) return;

    let cancelled = false;
    const syncAuth0User = async () => {
      try {
        if (!cancelled) setReady(false);
        const claims = await getIdTokenClaims();
        const idToken = String((claims as any)?.__raw || "").trim();
        if (!idToken) throw new Error("Auth0 did not return an ID token.");
        const nextUser = await hostedLoginWithAuth0({ idToken });
        const localIdentities = readStoredAuthIdentities();
        const existingIdentity = findIdentityByUserID(localIdentities, nextUser.id)
          || findIdentityByAccount(localIdentities, nextUser.username || nextUser.email, nextUser.id);
        if (!existingIdentity) {
          const identity = await generateIdentity();
          upsertStoredAuthIdentity({
            username: nextUser.username || normalizeUsername(nextUser.email),
            userId: nextUser.id,
            email: nextUser.email || undefined,
            publicKey: identity.publicKey,
            privateKeyJwk: identity.privateKeyJwk,
          });
        }
        if (!cancelled) {
          markAuth0LoggedOut(false);
          writeAuth0SwitchFromEmail("");
          persistUser(nextUser);
          syncTenantWithUser(nextUser.id);
        }
      } catch (error) {
        storeHostedAuth0Error(
          error instanceof Error && error.message.trim()
            ? error.message
            : "Hosted sign-in could not be completed. Please try again.",
        );
        if (!cancelled) persistUser(null);
      } finally {
        if (!cancelled) setReady(true);
      }
    };
    syncAuth0User();
    return () => {
      cancelled = true;
    };
  }, [auth0IsAuthenticated, auth0Loading, auth0User?.sub, getIdTokenClaims, auth0LoggedOut]);

  const value: AuthContextValue = {
    user,
    ready,
    login: async ({ identifier, password }) => {
      const loginIdentifier = String(identifier || "").trim();
      if (!loginIdentifier) throw new Error("Username or email is required.");
      const loginPassword = String(password || "").trim();

      const completeHostedPasswordLogin = async () => {
        const nextUser = await hostedLoginWithPassword({ identifier: loginIdentifier, password: loginPassword });
        const localIdentities = readStoredAuthIdentities();
        const existingIdentity = findIdentityByUserID(localIdentities, nextUser.id)
          || findIdentityByAccount(localIdentities, nextUser.username || loginIdentifier, nextUser.id);
        if (!existingIdentity) {
          const identity = await generateIdentity();
          upsertStoredAuthIdentity({
            username: nextUser.username || normalizeUsername(loginIdentifier),
            userId: nextUser.id,
            email: nextUser.email || undefined,
            publicKey: identity.publicKey,
            privateKeyJwk: identity.privateKeyJwk,
          });
        }
        persistUser(nextUser);
        syncTenantWithUser(nextUser.id);
        return;
      };

      if (isWalletCloudMode()) {
        await completeHostedPasswordLogin();
        return;
      }

      try {
        await completeHostedPasswordLogin();
        return;
      } catch (hostedError) {
        if (!shouldFallbackFromHostedPasswordAuth(hostedError)) {
          throw hostedError;
        }
      }

      const localIdentities = readStoredAuthIdentities();
      const hintedIdentity = findIdentityHintByIdentifier(localIdentities, loginIdentifier);
      const hintedPinnedBase = pinnedBaseForIdentity(hintedIdentity);
      const baseCandidates = loginDaemonBaseCandidates(localIdentities, hintedIdentity);
      const candidates = await expandLoginDaemonCandidates(baseCandidates);
      const strictPinnedOnly = !!hintedPinnedBase && candidates.length === 1;

      let challenge: {
        challenge_id: string;
        user_id?: string;
        username?: string;
        public_key?: string;
        has_identity_backup?: boolean;
        message: string;
      } | null = null;
      let authSettings: UISettings | undefined;
      let signingIdentity: StoredAuthIdentity | null = null;
      let discoveryError: unknown = null;
      let missingIdentityMessage = "";

      for (const candidate of candidates) {
        const candidateSettings = authSettingsForDaemonBase(candidate);
        try {
          const next = await apiRequest<{
            challenge_id: string;
            user_id?: string;
            username?: string;
            public_key?: string;
            has_identity_backup?: boolean;
            message: string;
          }>("/api/v1/auth/login/challenge", {
            method: "POST",
            body: { identifier: loginIdentifier },
          }, candidateSettings);
          const expectedUsername = normalizeUsername(String(next.username || ""));
          const expectedUserID = String(next.user_id || "").trim();
          const expectedPublicKey = String(next.public_key || "").trim();
          if (!expectedUsername) {
            discoveryError = new Error("Sign in failed because the server did not return an account username.");
            continue;
          }
          let matchedIdentity = expectedUserID
            ? findIdentityByUserID(localIdentities, expectedUserID)
            : findIdentityByAccount(localIdentities, expectedUsername);
          if (!matchedIdentity && expectedUserID && expectedPublicKey) {
            matchedIdentity = findIdentityByPublicKey(localIdentities, expectedPublicKey);
          }
          if (matchedIdentity && expectedPublicKey && String(matchedIdentity.publicKey || "").trim() !== expectedPublicKey) {
            matchedIdentity = null;
          }
          if (!matchedIdentity && next.has_identity_backup) {
            try {
              const recovered = await recoverIdentityFromDaemonBackup(
                String(next.challenge_id || ""),
                loginPassword,
                candidateSettings,
                expectedUsername,
                expectedUserID,
              );
              if (expectedPublicKey && String(recovered.publicKey || "").trim() !== expectedPublicKey) {
                throw new Error("Recovered identity does not match this account key. Import the correct identity backup and try again.");
              }
              upsertStoredAuthIdentity(recovered);
              localIdentities.push(recovered);
              matchedIdentity = recovered;
            } catch (recoveryErr) {
              discoveryError = recoveryErr;
              missingIdentityMessage = recoveryErr instanceof Error
                ? recoveryErr.message
                : "Account found, but this browser does not have its key. Import that account's identity backup and try again.";
              continue;
            }
          }
          if (!matchedIdentity) {
            missingIdentityMessage = !!next.has_identity_backup
              ? "Account found, but this browser does not have its key. Enter your password to restore it, or import that account's identity backup."
              : "Account found, but this browser does not have its key. Import that account's identity backup and try again.";
            discoveryError = new Error(missingIdentityMessage);
            continue;
          }
          challenge = next;
          authSettings = candidateSettings;
          signingIdentity = matchedIdentity;
          break;
        } catch (error) {
          discoveryError = error;
          if (shouldContinueLoginDiscovery(error)) {
            continue;
          }
          throw error;
        }
      }

      if (!challenge || !authSettings || !signingIdentity) {
        const mappedError = loginDiscoveryFailure(discoveryError, strictPinnedOnly, hintedPinnedBase);
        if (mappedError) throw mappedError;
        if (missingIdentityMessage) {
          throw new Error(missingIdentityMessage);
        }
        throw new Error("No local workspace with this account was reachable. Start the wallet app and try again.");
      }

      const signature = await signChallengeMessage(signingIdentity, challenge.message);
      const verified = await apiRequest<{
        user: any;
        session_token: string;
        session_expires_at: string;
        daemon_base_url?: string;
      }>("/api/v1/auth/login/verify", {
        method: "POST",
        body: {
          challenge_id: challenge.challenge_id,
          signature,
        },
      }, authSettings);

      const existing = readStoredUser();
      const nextUser = authUserFromServer(verified.user, existing);
      syncHubBaseWithAuthPayload(verified, nextUser.id);
      upsertStoredAuthIdentity({
        ...signingIdentity,
        username: nextUser.username || signingIdentity.username,
        userId: nextUser.id,
        email: nextUser.email || signingIdentity.email,
      });
      writeStoredAuthSession({
        token: verified.session_token,
        expiresAt: String(verified.session_expires_at || ""),
      });
      if (loginPassword) {
        const syncSettings = authSettingsForDaemonBase(
          normalizeDaemonBaseURL(String(verified.daemon_base_url || authSettings.hub_base || "")),
        );
        try {
          await syncIdentityBackupToDaemon({
            ...signingIdentity,
            username: nextUser.username || signingIdentity.username,
            userId: nextUser.id,
            email: nextUser.email || signingIdentity.email,
          }, loginPassword, syncSettings);
        } catch {
          // Best-effort only; local login should still succeed if daemon backup sync fails.
        }
      }
      persistUser(nextUser);
      syncTenantWithUser(nextUser.id);
    },
    signup: async ({ username, email, name, password }) => {
      const normalizedUsername = normalizeUsername(username || email);
      if (!normalizedUsername) throw new Error("A valid username is required.");
      const signupPassword = String(password || "").trim();
      if (!signupPassword) {
        throw new Error("Password is required so your encrypted identity recovery can be created.");
      }

      const identity = await generateIdentity();
      const normalizedEmail = String(email || "").trim().toLowerCase();
      const completeHostedPasswordSignup = async () => {
        const nextUser = await hostedSignupWithPassword({
          username: normalizedUsername,
          email: normalizedEmail,
          name: String(name || "").trim() || normalizedUsername,
          password: signupPassword,
        });
        upsertStoredAuthIdentity({
          username: nextUser.username || normalizedUsername,
          userId: nextUser.id,
          email: nextUser.email || normalizedEmail || undefined,
          publicKey: identity.publicKey,
          privateKeyJwk: identity.privateKeyJwk,
        });
        persistUser(nextUser);
        syncTenantWithUser(nextUser.id);
        return;
      };

      if (isWalletCloudMode()) {
        await completeHostedPasswordSignup();
        return;
      }

      try {
        await completeHostedPasswordSignup();
        return;
      } catch (hostedError) {
        if (!shouldFallbackFromHostedPasswordAuth(hostedError)) {
          throw hostedError;
        }
      }
      const encryptedIdentityBackup = await encryptIdentityBackup({
        version: 1,
        provider: "nebula-decentralized-ed25519",
        username: normalizedUsername,
        public_key: identity.publicKey,
        private_key_jwk: identity.privateKeyJwk,
        exported_at: new Date().toISOString(),
      }, signupPassword);
      const guestSettings = readUISettings();
      const explicitDaemonBase = normalizeDaemonBaseURL(String(guestSettings.hub_base || ""));
      const candidates = explicitDaemonBase
        ? [explicitDaemonBase]
        : daemonBaseCandidates();

      let payload: {
        user: any;
        session_token: string;
        session_expires_at: string;
        daemon_base_url?: string;
      } | null = null;
      let signupError: unknown = null;

      for (const candidate of candidates) {
        try {
          payload = await apiRequest<{
            user: any;
            session_token: string;
            session_expires_at: string;
            daemon_base_url?: string;
          }>("/api/v1/auth/signup", {
            method: "POST",
            body: {
              username: normalizedUsername,
              display_name: String(name || "").trim() || normalizedUsername,
              email: normalizedEmail,
              public_key: identity.publicKey,
              encrypted_identity_backup: encryptedIdentityBackup,
            },
          }, authSettingsForDaemonBase(candidate));
          break;
        } catch (error) {
          signupError = error;
          if (!explicitDaemonBase && shouldContinueSignupPlacement(error)) {
            continue;
          }
          throw error;
        }
      }

      if (!payload) {
        if (signupError instanceof APIError && signupError.code === "ERR_DAEMON_ALREADY_BOUND") {
          throw new Error(
            "Every reachable local workspace is already bound to another account. Start another wallet app instance and try again.",
          );
        }
        if (signupError instanceof Error) throw signupError;
        throw new Error("Could not reach the local wallet workspace needed to create this account.");
      }

      const nextUser = authUserFromServer(payload.user, null);
      syncHubBaseWithAuthPayload(payload, nextUser.id);
      upsertStoredAuthIdentity({
        username: nextUser.username || normalizedUsername,
        userId: nextUser.id,
        email: nextUser.email || normalizedEmail || undefined,
        publicKey: identity.publicKey,
        privateKeyJwk: identity.privateKeyJwk,
      });
      writeStoredAuthSession({
        token: payload.session_token,
        expiresAt: String(payload.session_expires_at || ""),
      });
      persistUser(nextUser);
      syncTenantWithUser(nextUser.id);
    },
    loginWithOAuth: async () => {
      if (!auth0Enabled()) {
        throw new Error("Hosted OAuth is not configured for this workspace yet.");
      }
      markAuth0LoggedOut(false);
      startHostedAuth0Redirect({ forceAccountSelection: true });
    },
    signupWithOAuth: async () => {
      if (!auth0Enabled()) {
        throw new Error("Hosted OAuth is not configured for this workspace yet.");
      }
      markAuth0LoggedOut(false);
      startHostedAuth0Redirect({ screenHint: "signup", forceAccountSelection: true });
    },
    loginWithGoogle: async () => {
      if (isWalletCloudMode()) {
        const config = await apiRequest<{
          enabled: boolean;
          client_id: string;
          redirect_uri?: string;
          redirect_uri_candidates?: string[];
          pkce_exchange_enabled?: boolean;
          legacy_id_token_enabled?: boolean;
        }>("/api/auth/google/config");
        if (!config?.enabled || !config?.client_id) {
          throw new Error("Google sign-in is not configured for the hosted wallet yet.");
        }
        const redirectCandidates = googleRedirectURICandidates(
          String(config.redirect_uri || ""),
          config.redirect_uri_candidates,
        );
        const candidates = redirectCandidates.length > 0 ? redirectCandidates : [String(config.redirect_uri || "")];
        let nextUser: AuthUser | null = null;
        let profile: { email: string; name: string } | null = null;
        let redirectMismatchError: Error | null = null;
        let popupRetryableError: Error | null = null;
        for (const candidate of candidates) {
          try {
            if (config.pkce_exchange_enabled) {
              const { code, codeVerifier, redirectURI } = await loginWithGooglePKCEPopup(
                String(config.client_id),
                candidate,
              );
              nextUser = await hostedLoginWithGoogleCode({ code, codeVerifier, redirectURI });
            } else {
              const idToken = await loginWithGoogleIDTokenFromPopup(String(config.client_id), candidate);
              profile = parseGoogleIDTokenProfile(idToken);
              nextUser = await hostedLoginWithGoogle({ idToken });
            }
            redirectMismatchError = null;
            popupRetryableError = null;
            break;
          } catch (error) {
            if (isOAuthRedirectURIMismatchError(error)) {
              redirectMismatchError = error instanceof Error ? error : new Error(String(error));
              continue;
            }
            if (isGoogleOAuthPopupRetryableError(error)) {
              popupRetryableError = error instanceof Error ? error : new Error(String(error));
              if (candidates.length > 1) continue;
            }
            throw error;
          }
        }
        if (!nextUser && redirectMismatchError) {
          throw new Error(`Google OAuth redirect URI mismatch. Add one of these URIs to your Google OAuth client: ${candidates.join(", ")}`);
        }
        if (!nextUser && popupRetryableError) {
          throw new Error(`Google login did not complete. If you saw redirect_uri_mismatch in the popup, add one of these URIs to your Google OAuth client: ${candidates.join(", ")}`);
        }
        if (!nextUser) {
          throw new Error("Google sign-in failed. No OAuth response was received.");
        }
        const localIdentities = readStoredAuthIdentities();
        const existingIdentity = findIdentityByUserID(localIdentities, nextUser.id)
          || findIdentityByAccount(localIdentities, nextUser.username || profile?.email || nextUser.email, nextUser.id);
        if (!existingIdentity) {
          const identity = await generateIdentity();
          upsertStoredAuthIdentity({
            username: nextUser.username || normalizeUsername(profile?.email || nextUser.email),
            userId: nextUser.id,
            email: nextUser.email || profile?.email || undefined,
            publicKey: identity.publicKey,
            privateKeyJwk: identity.privateKeyJwk,
          });
        }
        persistUser(nextUser);
        syncTenantWithUser(nextUser.id);
        return;
      }

      const config = await apiRequest<{
        enabled: boolean;
        client_id: string;
        redirect_uri?: string;
        redirect_uri_candidates?: string[];
        pkce_exchange_enabled?: boolean;
        legacy_id_token_enabled?: boolean;
      }>("/api/v1/auth/google/config");
      if (!config?.enabled || !config?.client_id) {
        throw new Error("Google login is not configured on this server.");
      }
      const redirectCandidates = googleRedirectURICandidates(
        String(config.redirect_uri || ""),
        config.redirect_uri_candidates,
      );
      let payload: {
        user: any;
        session_token: string;
        session_expires_at: string;
        daemon_base_url?: string;
      } | null = null;
      if (config.pkce_exchange_enabled) {
        let redirectMismatchError: Error | null = null;
        let popupRetryableError: Error | null = null;
        const candidates = redirectCandidates.length > 0 ? redirectCandidates : [String(config.redirect_uri || "")];
        for (const candidate of candidates) {
          try {
            const { code, codeVerifier, redirectURI } = await loginWithGooglePKCEPopup(
              String(config.client_id),
              candidate,
            );
            payload = await apiRequest<{
              user: any;
              session_token: string;
              session_expires_at: string;
              daemon_base_url?: string;
            }>("/api/v1/auth/google/exchange", {
              method: "POST",
              body: {
                code,
                code_verifier: codeVerifier,
                redirect_uri: redirectURI,
              },
            });
            redirectMismatchError = null;
            break;
          } catch (error) {
            if (isOAuthRedirectURIMismatchError(error)) {
              redirectMismatchError = error instanceof Error ? error : new Error(String(error));
              continue;
            }
            if (isGoogleOAuthPopupRetryableError(error)) {
              popupRetryableError = error instanceof Error ? error : new Error(String(error));
              if (candidates.length > 1) {
                continue;
              }
            }
            throw error;
          }
        }
        if (!payload && redirectMismatchError) {
          throw new Error(
            `Google OAuth redirect URI mismatch. Add one of these URIs to your Google OAuth client: ${candidates.join(", ")}`,
          );
        }
        if (!payload && popupRetryableError) {
          throw new Error(
            `Google login did not complete. If you saw redirect_uri_mismatch in the popup, add one of these URIs to your Google OAuth client: ${candidates.join(", ")}`,
          );
        }
      } else if (config.legacy_id_token_enabled) {
        let redirectMismatchError: Error | null = null;
        let popupRetryableError: Error | null = null;
        const candidates = redirectCandidates.length > 0 ? redirectCandidates : [String(config.redirect_uri || "")];
        for (const candidate of candidates) {
          try {
            const idToken = await loginWithGoogleIDTokenFromPopup(
              String(config.client_id),
              candidate,
            );
            payload = await apiRequest<{
              user: any;
              session_token: string;
              session_expires_at: string;
              daemon_base_url?: string;
            }>("/api/v1/auth/google", {
              method: "POST",
              body: { id_token: idToken },
            });
            redirectMismatchError = null;
            break;
          } catch (error) {
            if (isOAuthRedirectURIMismatchError(error)) {
              redirectMismatchError = error instanceof Error ? error : new Error(String(error));
              continue;
            }
            if (isGoogleOAuthPopupRetryableError(error)) {
              popupRetryableError = error instanceof Error ? error : new Error(String(error));
              if (candidates.length > 1) {
                continue;
              }
            }
            throw error;
          }
        }
        if (!payload && redirectMismatchError) {
          throw new Error(
            `Google OAuth redirect URI mismatch. Add one of these URIs to your Google OAuth client: ${candidates.join(", ")}`,
          );
        }
        if (!payload && popupRetryableError) {
          throw new Error(
            `Google login did not complete. If you saw redirect_uri_mismatch in the popup, add one of these URIs to your Google OAuth client: ${candidates.join(", ")}`,
          );
        }
      } else {
        throw new Error("Google login is not fully configured on this server.");
      }
      if (!payload) {
        throw new Error("Google login failed. No OAuth response was received.");
      }

      const existing = readStoredUser();
      const nextUser = authUserFromServer(payload.user, existing);
      syncHubBaseWithAuthPayload(payload, nextUser.id);
      writeStoredAuthSession({
        token: payload.session_token,
        expiresAt: String(payload.session_expires_at || ""),
      });
      persistUser(nextUser);
      syncTenantWithUser(nextUser.id);
    },
    loginWithGitHub: async () => {
      if (!isWalletCloudMode()) {
        throw new Error("GitHub login is only available in the hosted wallet right now.");
      }
      const config = await apiRequest<{
        enabled: boolean;
        client_id: string;
        redirect_uri?: string;
        redirect_uri_candidates?: string[];
      }>("/api/auth/github/config");
      if (!config?.enabled || !config?.client_id) {
        throw new Error("GitHub sign-in is not configured for the hosted wallet yet.");
      }
      const redirectCandidates = googleRedirectURICandidates(
        String(config.redirect_uri || ""),
        config.redirect_uri_candidates,
      );
      const candidates = redirectCandidates.length > 0 ? redirectCandidates : [String(config.redirect_uri || "")];
      let redirectMismatchError: Error | null = null;
      let nextUser: AuthUser | null = null;
      for (const candidate of candidates) {
        try {
          const { code, codeVerifier, redirectURI } = await loginWithGitHubPKCEPopup(
            String(config.client_id),
            candidate,
          );
          nextUser = await hostedLoginWithGitHub({ code, codeVerifier, redirectURI });
          redirectMismatchError = null;
          break;
        } catch (error) {
          if (isOAuthRedirectURIMismatchError(error)) {
            redirectMismatchError = error instanceof Error ? error : new Error(String(error));
            continue;
          }
          throw error;
        }
      }
      if (!nextUser && redirectMismatchError) {
        throw new Error(`GitHub OAuth redirect URI mismatch. Add one of these URIs to your GitHub OAuth app: ${candidates.join(", ")}`);
      }
      if (!nextUser) {
        throw new Error("GitHub sign-in failed. No OAuth response was received.");
      }
      const localIdentities = readStoredAuthIdentities();
      const existingIdentity = findIdentityByUserID(localIdentities, nextUser.id)
        || findIdentityByAccount(localIdentities, nextUser.username || nextUser.email, nextUser.id);
      if (!existingIdentity) {
        const identity = await generateIdentity();
        upsertStoredAuthIdentity({
          username: nextUser.username || normalizeUsername(nextUser.email),
          userId: nextUser.id,
          email: nextUser.email || undefined,
          publicKey: identity.publicKey,
          privateKeyJwk: identity.privateKeyJwk,
        });
      }
      persistUser(nextUser);
      syncTenantWithUser(nextUser.id);
    },
    logout: async () => {
      resetWalletBetaDisclaimerAcceptance();
      if (auth0Enabled()) {
        const previousUserID = String(user?.id || "").trim();
        await performHostedAuth0Logout({
          previousUserID,
          markLoggedOut: markAuth0LoggedOut,
          persistUser,
          clearTenantBinding,
        });
        return;
      }
      const session = readStoredAuthSession();
      if (session?.token) {
        try {
          await apiRequest("/api/v1/auth/logout", {
            method: "POST",
            headers: { Authorization: `Bearer ${session.token}` },
          });
        } catch {
          // local logout still proceeds
        }
      }
      const previousUserID = String(user?.id || "").trim();
      writeStoredAuthSession(null);
      persistUser(null);
      clearTenantBinding(previousUserID);
    },
    exportIdentityBackup: async (passphrase: string) => {
      const identity = readStoredAuthIdentity();
      if (!identity) throw new Error("No local decentralized identity found to export.");
      return encryptIdentityBackup(toIdentityBackup(identity), passphrase);
    },
    importIdentityBackup: async (raw: string, passphrase?: string) => {
      await ensureWebCrypto();
      const imported = await decryptIdentityBackup(raw, passphrase);
      deriveEd25519KeyPair(imported);
      writeStoredAuthIdentity(imported);
      return imported.username;
    },
    completeOnboarding: (daemon) => {
      if (!user) return;
      persistUser({ ...user, daemon });
    },
    updateUser: (patch) => {
      if (!user) return;
      if (patch.name !== undefined) {
        writeProfileNameOverride(user.id, patch.name);
      }
      persistUser({ ...user, ...patch });
    },
    updateDaemon: (patch) => {
      if (!user) return;
      const base = user.daemon ?? DEFAULT_DAEMON;
      persistUser({ ...user, daemon: { ...base, ...patch } });
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
